import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getControls, listControlAssignmentUsers, updateControl } from "../../api/controls.api.js";
import { canWriteControls } from "../../accessControl";
import { listTaxonomyValues } from "../../api/taxonomy.api";
import ControlBulkUploadModal from "../../components/controls/ControlBulkUploadModal";
import ControlFilters from "../../components/controls/ControlFilters";
import ControlsHealthPanel from "../../components/controls/ControlsHealthPanel";
import ControlTable from "../../components/controls/ControlTable";
import ControlCreateModal from "../../components/controls/ControlCreateModal";
import Loader from "../../components/common/Loader";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { colors, spacing, typography } from "../../ui/tokens";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { useDestructiveAction } from "../../destructiveActions";
import ControlDetailDrawer from "../../ControlDetailDrawer";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  sectionHeaderStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import { trackUiEvent } from "../../utils/analytics";
import { useAuth } from "../../useAuth";

const CONTROL_LABEL_OPTIONS = ["Preventative", "Detective", "Corrective"];
const CONTROL_STATUS_OPTIONS = ["Active", "Draft", "Open", "In Progress", "Closed", "Archived"];

const normalizeControlLabels = (value) => {
  if (!value) return [];
  const source = Array.isArray(value) ? value : String(value).split(";");
  const normalized = [];
  source.forEach((item) => {
    const label = String(item || "").trim();
    if (!CONTROL_LABEL_OPTIONS.includes(label)) return;
    if (!normalized.includes(label)) {
      normalized.push(label);
    }
  });
  return normalized;
};

const containerStyle = {
  ...pageShellStyle,
  gap: spacing.xl + spacing.sm,
  alignContent: "start",
};

const cardStyle = {
  ...surfaceCardStyle,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};

const cardHeaderStyle = {
  ...surfaceCardHeaderStyle,
  alignItems: "center",
};

const filterBarWrapStyle = {
  ...surfaceCardBodyStyle,
  borderBottom: `1px solid ${colors.border}`,
};

const tableWrapStyle = {
  ...surfaceCardBodyStyle,
  flex: 1,
  minHeight: 0,
  display: "grid",
  gap: spacing.xl,
};

const pageSummaryTextStyle = {
  fontSize: typography.size.xs,
  color: colors.textSubtle,
};

const buildUserOptions = (items) => {
  const options = [];
  const seen = new Set();
  const list = Array.isArray(items) ? items : [];
  list.forEach((user) => {
    const value = user?.id != null ? String(user.id) : "";
    if (!value || seen.has(value)) return;
    const label = user?.username
      ? `${user.username}${user.email ? ` (${user.email})` : ""}`
      : user?.email || `User #${user.id}`;
    seen.add(value);
    options.push({ value, label });
  });
  return options;
};

const filterOptions = (options, query) => {
  const term = String(query || "").trim().toLowerCase();
  if (!term) return options;
  return options.filter((opt) => String(opt.label || opt.value || "").toLowerCase().includes(term));
};

const getTodayDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

export default function ControlsDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const canManageControls = canWriteControls(user);
  const [controls, setControls] = useState([]);
  const [totalControls, setTotalControls] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("register");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const [category, setCategory] = useState("");
  const [catalog, setCatalog] = useState("");
  const [asOf, setAsOf] = useState(() => getTodayDateInputValue());
  const [sort, setSort] = useState("");
  const [healthSelection, setHealthSelection] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const hasFilters = Boolean(search || status || owner || category || catalog || healthSelection);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchOwner, setUserSearchOwner] = useState("");
  const [userSearchAssignee, setUserSearchAssignee] = useState("");
  const [controlCategories, setControlCategories] = useState([]);
  const [categoryError, setCategoryError] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [editingControl, setEditingControl] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [confirmPruneOpen, setConfirmPruneOpen] = useState(false);
  const [pendingEditPayload, setPendingEditPayload] = useState(null);
  const [drawerControlId, setDrawerControlId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const { action: linkPruneAction } = useDestructiveAction("CONTROL_LINK_PRUNE_ON_UPDATE");
  useEffect(() => {
    const handler = (e) => {
      const ctrl = e.detail || {};
      setEditingControl(ctrl);
      setEditForm({
        name: ctrl.name || "",
        description: ctrl.description || "",
        status: ctrl.status || "",
        category: ctrl.category || "",
        owner_id: ctrl.owner_id || "",
        assignee_id: ctrl.assignee_id || "",
        owner_user_id: ctrl.owner_user_id ? String(ctrl.owner_user_id) : "",
        assignee_user_id: ctrl.assignee_user_id ? String(ctrl.assignee_user_id) : "",
        control_category_id: ctrl.control_category_id ? String(ctrl.control_category_id) : "",
        control_labels: normalizeControlLabels(ctrl.control_labels),
        catalog_id: ctrl.catalog_id || "",
      });
      setEditError("");
      setEditLoading(false);
    };
    window.addEventListener("openControlEditInline", handler);
    return () => window.removeEventListener("openControlEditInline", handler);
  }, []);

  useEffect(() => {
    if (!canManageControls) {
      setUserOptions([]);
      return undefined;
    }
    let active = true;
    const loadUsers = async () => {
      try {
        const res = await listControlAssignmentUsers();
        const payload = res?.data || [];
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        if (active) {
          setUserOptions(buildUserOptions(list));
        }
      } catch (_err) {
        if (active) {
          setUserOptions([]);
        }
      }
    };
    loadUsers();
    return () => {
      active = false;
    };
  }, [canManageControls]);

  useEffect(() => {
    const drawerFromState = Number(location.state?.openDrawerControlId);
    const hasDrawer = Number.isInteger(drawerFromState) && drawerFromState > 0;
    if (!hasDrawer) return;

    setDrawerControlId(drawerFromState);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      setCategoryError("");
      try {
        const res = await listTaxonomyValues("control_category");
        const payload = res?.data || res;
        if (active) {
          setControlCategories(Array.isArray(payload) ? payload : []);
        }
      } catch (err) {
        if (active) {
          setControlCategories([]);
          setCategoryError(err?.response?.data?.detail || err?.message || "Unable to load categories.");
        }
      }
    };
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const applyEditSubmit = async (payload) => {
    if (!editingControl?.id || !payload) return;
    setEditLoading(true);
    setEditError("");
    try {
      await updateControl(editingControl.id, payload);
      await handleManualRefresh();
      setEditingControl(null);
      setEditForm(null);
    } catch (err) {
      setEditError(err?.message || "Failed to update control");
    } finally {
      setEditLoading(false);
    }
  };

  const requestEditSubmit = () => {
    if (!editingControl?.id || !editForm) return;
    if (!editForm.name) {
      setEditError("Name is required");
      return;
    }
    const payload = {
      ...editForm,
      owner_user_id:
        editForm.owner_user_id === "" || editForm.owner_user_id === undefined
          ? null
          : Number(editForm.owner_user_id),
      assignee_user_id:
        editForm.assignee_user_id === "" || editForm.assignee_user_id === undefined
          ? null
          : Number(editForm.assignee_user_id),
      control_category_id:
        editForm.control_category_id === "" || editForm.control_category_id === undefined
          ? null
          : Number(editForm.control_category_id),
      control_labels: normalizeControlLabels(editForm.control_labels),
      catalog_id: editForm.catalog_id || null,
    };
    setPendingEditPayload(payload);
    setConfirmPruneOpen(true);
  };

  const updateFilter = (field, setter, { clearHealth = true } = {}) => (value) => {
    setter(value);
    setPage(1);
    if (clearHealth) {
      setHealthSelection(null);
    }
    trackUiEvent("controls_filters_changed", {
      field,
      value: value || null,
    });
  };

  const handleSearchChange = updateFilter("search", setSearch);
  const handleStatusChange = updateFilter("status", setStatus);
  const handleOwnerChange = updateFilter("owner", setOwner);
  const handleCategoryChange = updateFilter("category", setCategory);
  const handleCatalogChange = updateFilter("catalog", setCatalog);
  const handleAsOfChange = updateFilter("as_of", setAsOf, { clearHealth: false });
  const handleSortChange = updateFilter("sort", setSort, { clearHealth: false });

  const fetchControls = async () => {
    setLoading(true);
    setError(null);
    try {
      const hasOwnerOverride =
        healthSelection && Object.prototype.hasOwnProperty.call(healthSelection, "owner");
      const params = {
        q: search || undefined,
        status: status || undefined,
        owner: hasOwnerOverride ? healthSelection.owner || undefined : owner || undefined,
        category: category || undefined,
        catalog: catalog || undefined,
        health_reason: healthSelection?.healthReason || undefined,
        as_of: asOf || undefined,
        sort: sort || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      const res = await getControls(params);
      const payload = res?.data ?? res;
      const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
      setControls(items);
      setTotalControls(Number.isFinite(payload?.total) ? payload.total : items.length);
    } catch (err) {
      setError(err?.message || "Failed to load controls");
      setControls([]);
      setTotalControls(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchControls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, owner, category, catalog, sort, page, healthSelection, asOf]);

  useEffect(() => {
    if (activeTab !== "health") return;
    trackUiEvent("controls_health_tab_viewed", { source: "controls_dashboard" });
  }, [activeTab]);

  const registerFilters = useMemo(
    () => ({
      search,
      status,
      owner,
      category,
      catalog,
    }),
    [search, status, owner, category, catalog]
  );
  const healthFilters = useMemo(
    () => ({
      ...registerFilters,
      asOf,
    }),
    [asOf, registerFilters]
  );

  const totalPages = Math.max(1, Math.ceil(totalControls / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleManualRefresh = async () => {
    await fetchControls();
    setRefreshNonce((current) => current + 1);
  };

  const handleHealthDrillDown = (selection) => {
    setHealthSelection(selection || null);
    setPage(1);
    setActiveTab("register");
  };

  const clearHealthScope = () => {
    setHealthSelection(null);
    setPage(1);
  };

  const onRowClick = (c) => {
    if (!c?.id) return;
    setDrawerControlId(c.id);
  };

  const categoryOptions = useMemo(() => {
    const list = Array.isArray(controlCategories) ? controlCategories : [];
    return list
      .filter((item) => String(item?.status || "active").toLowerCase() === "active")
      .map((item) => ({
        value: String(item.id),
        label: item.label || item.value || `Category #${item.id}`,
      }));
  }, [controlCategories]);

  const editCategoryOptions = useMemo(() => {
    const list = Array.isArray(controlCategories) ? controlCategories : [];
    const active = list.filter((item) => String(item?.status || "active").toLowerCase() === "active");
    const currentId = editForm?.control_category_id;
    const current =
      currentId && !active.some((item) => String(item.id) === String(currentId))
        ? list.find((item) => String(item.id) === String(currentId))
        : null;
    const combined = current ? [current, ...active] : active;
    return combined.map((item) => ({
      value: String(item.id),
      label: item.label || item.value || `Category #${item.id}`,
    }));
  }, [controlCategories, editForm?.control_category_id]);

  const ownerOptions = useMemo(() => [{ value: "", label: "Unassigned" }, ...userOptions], [userOptions]);
  const filterOwnerOptions = useMemo(() => {
    const current =
      owner && !userOptions.some((option) => String(option.value) === String(owner))
        ? [{ value: owner, label: owner }]
        : [];
    return [...current, ...userOptions];
  }, [owner, userOptions]);
  const filteredOwnerOptions = useMemo(
    () => filterOptions(ownerOptions, userSearchOwner),
    [ownerOptions, userSearchOwner]
  );
  const filteredAssigneeOptions = useMemo(
    () => filterOptions(ownerOptions, userSearchAssignee),
    [ownerOptions, userSearchAssignee]
  );
  const statusOptions = useMemo(() => {
    const seen = new Set(CONTROL_STATUS_OPTIONS.map((option) => option.toLowerCase()));
    const merged = [...CONTROL_STATUS_OPTIONS];
    (Array.isArray(controls) ? controls : []).forEach((control) => {
      const value = String(control?.status || "").trim();
      if (!value || seen.has(value.toLowerCase())) return;
      seen.add(value.toLowerCase());
      merged.push(value);
    });
    if (status && !seen.has(String(status).toLowerCase())) {
      merged.push(status);
    }
    return merged;
  }, [controls, status]);
  const registerSummaryLabel = healthSelection?.label || (hasFilters ? "Filtered results" : "All controls");
  const healthScopeDescription = healthSelection
    ? Object.prototype.hasOwnProperty.call(healthSelection, "owner")
      ? `Showing controls for ${healthSelection.label}. The owner scope comes from the health selection; other register filters still apply.`
      : `Showing controls for ${healthSelection.label}. Existing register filters still define the overall scope.`
    : "";

  return (
    <div style={containerStyle}>
      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h1 style={pageTitleStyle}>Controls</h1>
          <p style={pageSubtitleStyle}>Manage control records, ownership, and lifecycle status.</p>
        </div>
        <div style={pageHeaderActionsStyle}>
          {canManageControls && (
            <>
              <Button onClick={() => setCreateModalOpen(true)} variant="primary">
                + Create control
              </Button>
              <Button onClick={() => setBulkUploadOpen(true)} variant="secondary">
                Bulk Upload Controls
              </Button>
            </>
          )}
          <Button onClick={handleManualRefresh} variant="secondary">
            Refresh
          </Button>
        </div>
      </div>

      <div style={tabRowStyle} role="tablist" aria-label="Controls views">
        {[
          { key: "register", label: "Controls Register" },
          { key: "health", label: "Controls Health" },
        ].map((tab) => (
          <Button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            variant="ghost"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: `${spacing.sm}px ${spacing.md}px`,
              borderBottom: activeTab === tab.key ? `2px solid ${colors.accent}` : "2px solid transparent",
              borderRadius: 0,
              color: activeTab === tab.key ? colors.accent : "#475467",
              boxShadow: "none",
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {activeTab === "register" && (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                  Controls list
                </div>
                <div style={{ fontWeight: typography.weight.medium, fontSize: 16, color: colors.text }}>
                  {totalControls} control{totalControls === 1 ? "" : "s"}
                </div>
              </div>
              <div style={pageSummaryTextStyle}>
                {registerSummaryLabel}
              </div>
            </div>
          </div>

          <div style={filterBarWrapStyle}>
            <ControlFilters
              search={search}
              onSearchChange={handleSearchChange}
              status={status}
              onStatusChange={handleStatusChange}
              statusOptions={statusOptions}
              owner={owner}
              onOwnerChange={handleOwnerChange}
              ownerOptions={filterOwnerOptions}
              category={category}
              onCategoryChange={handleCategoryChange}
              categoryOptions={categoryOptions}
              categoryError={categoryError}
              catalog={catalog}
              onCatalogChange={handleCatalogChange}
              sort={sort}
              onSortChange={handleSortChange}
            />
          </div>

          <div style={tableWrapStyle}>
            {healthSelection && (
              <div style={healthScopeBannerStyle}>
                <div style={{ display: "grid", gap: 2 }}>
                  <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>
                    Health drill-down active
                  </div>
                  <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
                    {healthScopeDescription}
                  </div>
                </div>
                <Button type="button" variant="secondary" onClick={clearHealthScope}>
                  Clear
                </Button>
              </div>
            )}
            {loading && <Loader label="Loading controls..." />}
            {error && !loading && <div style={{ color: "#b91c1c" }}>{error}</div>}
            {!loading && !error && totalControls === 0 && !hasFilters && (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 24,
                  background: "transparent",
                  display: "grid",
                  gap: spacing.xs,
                  maxWidth: 560,
                  width: "100%",
                  margin: "24px auto 0",
                  minHeight: 140,
                  alignContent: "start",
                }}
              >
                <div style={{ fontWeight: 600 }}>No controls yet</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  Create a control to document safeguards and link them to risks.
                </div>
                {canManageControls && (
                  <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                    <Button onClick={() => setCreateModalOpen(true)} variant="primary" style={{ padding: "6px 10px", fontSize: 12 }}>
                      Create your first control
                    </Button>
                  </div>
                )}
              </div>
            )}
            {!loading && !error && totalControls === 0 && hasFilters && (
              <div style={{ color: "#6b7280" }}>No controls match your filters.</div>
            )}
            {!loading && !error && totalControls > 0 && (
              <ControlTable controls={controls} onRowClick={onRowClick} />
            )}
          </div>

          <div
            style={{
              ...toolbarStyle,
              justifyContent: "space-between",
              borderTop: `1px solid ${colors.border}`,
              padding: `${spacing.md}px ${spacing.xl + spacing.sm}px`,
            }}
          >
            <span style={{ color: "#6b7280", fontSize: typography.size.xs }}>
              Showing {controls.length} of {totalControls}
            </span>
            <div style={toolbarStyle}>
              <Button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                variant="secondary"
              >
                Prev
              </Button>
              <span style={{ fontSize: typography.size.xs, color: "#4b5563" }}>
                Page {page} / {totalPages}
              </span>
              <Button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                variant="secondary"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "health" && (
        <ControlsHealthPanel
          active
          filters={healthFilters}
          filterContent={(
            <ControlFilters
              search={search}
              onSearchChange={handleSearchChange}
              status={status}
              onStatusChange={handleStatusChange}
              statusOptions={statusOptions}
              owner={owner}
              onOwnerChange={handleOwnerChange}
              ownerOptions={filterOwnerOptions}
              category={category}
              onCategoryChange={handleCategoryChange}
              categoryOptions={categoryOptions}
              categoryError={categoryError}
              catalog={catalog}
              onCatalogChange={handleCatalogChange}
              asOf={asOf}
              onAsOfChange={handleAsOfChange}
              showSort={false}
            />
          )}
          refreshNonce={refreshNonce}
          onRefresh={handleManualRefresh}
          onOpenControl={setDrawerControlId}
          onDrillDown={handleHealthDrillDown}
        />
      )}

      {editingControl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2600,
          }}
          onClick={() => !editLoading && setEditingControl(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 10, padding: spacing.lg * 2, width: "min(640px, 95vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: spacing.xl }}>Edit Control</h3>
            {linkPruneAction && (
              <div
                style={{
                  border: "1px solid #fde68a",
                  background: "#fffbeb",
                  color: "#92400e",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 12,
                  marginBottom: spacing.md,
                }}
              >
                <div style={{ fontWeight: 600 }}>Implicit replacement</div>
                <div>{linkPruneAction.description}</div>
                <div>Scope: {linkPruneAction.scope}</div>
                <div>Omitted linked risks will be removed when you save.</div>
              </div>
            )}
            <div style={{ display: "grid", gap: spacing.lg, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
              <Input
                placeholder="Name *"
                value={editForm?.name || ""}
                onChange={(e) => handleEditChange("name", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              />
              <select
                value={editForm?.status || ""}
                onChange={(e) => handleEditChange("status", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <select
                value={editForm?.control_category_id || ""}
                onChange={(e) => handleEditChange("control_category_id", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              >
                <option value="">{categoryError || "Control Category"}</option>
                {editCategoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Owner search"
                value={userSearchOwner}
                onChange={(e) => setUserSearchOwner(e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              />
              <select
                value={editForm?.owner_user_id || ""}
                onChange={(e) => handleEditChange("owner_user_id", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              >
                {filteredOwnerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Assignee search"
                value={userSearchAssignee}
                onChange={(e) => setUserSearchAssignee(e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              />
              <select
                value={editForm?.assignee_user_id || ""}
                onChange={(e) => handleEditChange("assignee_user_id", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              >
                {filteredAssigneeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Catalog ID"
                value={editForm?.catalog_id || ""}
                onChange={(e) => handleEditChange("catalog_id", e.target.value)}
                style={{ padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db" }}
              />
              <div style={{ gridColumn: "1 / -1", display: "grid", gap: spacing.xs }}>
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Control labels</div>
                <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                  {CONTROL_LABEL_OPTIONS.map((label) => {
                    const selected = normalizeControlLabels(editForm?.control_labels).includes(label);
                    return (
                      <label
                        key={label}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: spacing.xs,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 999,
                          padding: "4px 10px",
                          background: selected ? colors.surfaceSubtle : colors.surface,
                          color: colors.text,
                          fontSize: typography.size.xs,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            const current = normalizeControlLabels(editForm?.control_labels);
                            const next = selected
                              ? current.filter((item) => item !== label)
                              : [...current, label];
                            handleEditChange("control_labels", next);
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <textarea
                placeholder="Description"
                value={editForm?.description || ""}
                onChange={(e) => handleEditChange("description", e.target.value)}
                style={{ gridColumn: "1 / -1", padding: `${spacing.lg}px ${spacing.xl}px`, borderRadius: 8, border: "1px solid #d1d5db", minHeight: 80 }}
              />
            </div>
            {editError && <div style={{ color: "#b91c1c", marginTop: spacing.md }}>{editError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.md, marginTop: spacing.md + spacing.sm }}>
              <Button
                onClick={() => !editLoading && setEditingControl(null)}
                variant="secondary"
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={requestEditSubmit}
                variant="primary"
                disabled={editLoading}
                style={{ opacity: editLoading ? 0.6 : 1 }}
              >
                {editLoading ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <DestructiveConfirm
        actionId="CONTROL_LINK_PRUNE_ON_UPDATE"
        open={confirmPruneOpen}
        resourceDisplayName={editingControl?.name}
        context={{ scopeLabel: "Saving control updates may prune unlinked risks." }}
        backdropStyle={{ zIndex: 3200 }}
        onCancel={() => {
          setConfirmPruneOpen(false);
          setPendingEditPayload(null);
        }}
        onConfirm={() => {
          const payload = pendingEditPayload;
          setConfirmPruneOpen(false);
          setPendingEditPayload(null);
          if (payload) {
            applyEditSubmit(payload);
          }
        }}
      />
      <ControlDetailDrawer
        controlId={drawerControlId}
        onClose={() => setDrawerControlId(null)}
      />
      <ControlCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          handleManualRefresh();
        }}
      />
      <ControlBulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onImported={() => {
          handleManualRefresh();
        }}
      />
    </div>
  );
}

const tabRowStyle = {
  ...toolbarStyle,
  borderBottom: `1px solid ${colors.border}`,
};

const healthScopeBannerStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: 12,
  padding: `${spacing.md}px ${spacing.lg}px`,
  background: colors.surfaceSubtle,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.md,
  flexWrap: "wrap",
};
